import React, { Component, useState } from 'react';
import { FormattedMessage, useIntl, injectIntl, IntlShape } from 'react-intl';
import { Score, sendFeedback, updateFeedback, type DocumentReference } from 'lib/firebase';

import { Button, TextArea } from '@carbon/react';

import { FaceActivated, FaceNeutral, FaceSatisfied } from '@carbon/icons-react';
import * as css from './Feedback.module.scss';

function Questions(props: { onYep(): void; onMeh(): void; onNope(): void }) {
    return (
        <div className={css.topRow}>
            <div className={css.title} children={<FormattedMessage defaultMessage="Was this helpful?" />} />

            <div className={css.buttons}>
                <Button
                    kind="tertiary"
                    renderIcon={FaceActivated}
                    children={<FormattedMessage defaultMessage="Yes, thanks!" />}
                    onClick={props.onYep}
                />
                <Button
                    kind="tertiary"
                    renderIcon={FaceNeutral}
                    children={<FormattedMessage defaultMessage="Sort of, thanks!" />}
                    onClick={props.onMeh}
                />
                <Button
                    kind="tertiary"
                    renderIcon={FaceSatisfied}
                    children={<FormattedMessage defaultMessage="Not really" />}
                    onClick={props.onNope}
                />
            </div>
        </div>
    );
}
function Thanks() {
    return (
        <div className={css.topRow}>
            <div className={css.title} children={<FormattedMessage defaultMessage="Thanks for your feedback!" />} />
        </div>
    );
}
function Message(props: { onSubmit(message: string): void; isLoading: boolean }) {
    const intl = useIntl();
    const [message, setMessage] = useState<string>('');

    return (
        <form className={css.messageWrapper}>
            <div className={css.topRow}>
                <div className={css.title} children={<FormattedMessage defaultMessage="Sorry about that" />} />
            </div>

            <TextArea
                id="feedback-message"
                ref={c => {
                    if (c instanceof HTMLTextAreaElement) c.focus();
                }}
                rows={6}
                enableCounter
                maxCount={300}
                disabled={props.isLoading}
                labelText=""
                placeholder={intl.formatMessage({ defaultMessage: 'Any suggestions how to improve it?' })}
                value={message}
                onChange={e => setMessage(e.target.value)}
                className={css.textArea}
            />
            <Button
                kind="primary"
                onClick={() => props.onSubmit(message)}
                children={intl.formatMessage({ defaultMessage: 'Submit Feedback' })}
            />
        </form>
    );
}

type State = {
    hasVoted: boolean;
    score: Score;

    isSaving: boolean;
    isDone: boolean;
    docRef: null | DocumentReference;
    error: null | string;
};
const getInitialState = (): State => ({
    hasVoted: false,
    score: Score.meh,

    isSaving: false,
    isDone: false,
    docRef: null,
    error: null,
});

type Props = {
    disabled?: boolean;
    intl: IntlShape;
};

class View extends Component<Props, State> {
    readonly state = getInitialState();

    private handleVote = (score: Score): void => {
        const { formatMessage } = this.props.intl;

        this.setState({ hasVoted: true, score, isSaving: true }, () => {
            sendFeedback(score, '').then(
                docRef => {
                    this.setState({
                        hasVoted: true,
                        isSaving: false,
                        docRef,
                    });
                },
                e => {
                    console.log('Failed to save user feedback', e);
                    this.setState({
                        isSaving: false,
                        hasVoted: true,
                        error: e?.message ?? formatMessage({ defaultMessage: 'Failed to submit message' }),
                    });
                },
            );
        });
    };
    private handleMessage = (message: string): void => {
        const { formatMessage } = this.props.intl;
        const { docRef } = this.state;

        if (docRef) {
            updateFeedback(docRef, { message }).then(
                () => this.setState({ isDone: true }),
                e => {
                    this.setState({
                        isDone: true,
                        error:
                            e?.message ??
                            formatMessage({
                                defaultMessage: 'Failed to submit feedback message!',
                            }),
                    });
                },
            );
        } else {
            this.setState({
                isDone: true,
                error: formatMessage({
                    defaultMessage: 'Cannot send feedback without entry reference!',
                }),
            });
        }
    };

    render() {
        const { disabled } = this.props;
        const { isSaving, hasVoted, isDone, score } = this.state;

        if (disabled) return null;
        let body: ReactNode;

        // Say what?!
        if (!hasVoted) {
            body = (
                <Questions
                    onYep={() => this.handleVote(Score.yep)}
                    onMeh={() => this.handleVote(Score.meh)}
                    onNope={() => this.handleVote(Score.nope)}
                />
            );
        }

        // Sorry, tell us why
        else if (score !== Score.yep && !isDone) {
            body = <Message isLoading={isSaving} onSubmit={this.handleMessage} />;
        }

        // Thanks
        else {
            body = <Thanks />;
        }

        return (
            <section className={css.root}>
                <hr />
                <div className={css.innerWrapper} children={body} />
            </section>
        );
    }
}

export const Feedback = injectIntl(View);
